import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { Bandeau, Champ, Modale } from './ui';
import type { Client, Verdict } from '../api/types';

const VIDE = {
  type: 'particulier', nom: '', prenom: '', raisonSociale: '',
  dateNaissance: '', lieuNaissance: '', telephone: '', email: '', adresse: '',
  pieceType: 'CIN', pieceNumero: '', pieceDelivreeLe: '', pieceDelivreeA: '',
  permisNumero: '', permisDelivreLe: '', compteATerme: false,
};

const jour = (d?: string) => (d ? String(d).slice(0, 10) : '');

/**
 * Creation ET modification de client.
 *
 * Le permis est capture SEPAREMENT de la piece d'identite : le contrat papier
 * n'a qu'un champ pour les deux, et sans date de permis l'article 01 est
 * inverifiable. Le contrat imprime, lui, garde la ligne unique.
 *
 * La modification existe pour une raison precise : une date de naissance mal
 * saisie bloque le client a l'article 01 a chaque location, pour toujours. La
 * demande de correction pouvait etre accordee — mais rien, nulle part, ne
 * permettait ensuite de changer la valeur.
 */
export function FormulaireClient({ client, onCree, onFermer }:
  { client?: Client; onCree: (c: Client, v: Verdict) => void; onFermer: () => void }) {
  const modification = Boolean(client);
  const [f, setF] = useState(() => (client
    ? {
      ...VIDE,
      type: client.type ?? 'particulier',
      nom: client.nom ?? '', prenom: client.prenom ?? '', raisonSociale: client.raisonSociale ?? '',
      dateNaissance: jour(client.dateNaissance), lieuNaissance: client.lieuNaissance ?? '',
      telephone: client.telephone ?? '', email: client.email ?? '', adresse: client.adresse ?? '',
      pieceType: client.pieceType ?? 'CIN', pieceNumero: client.pieceNumero ?? '',
      pieceDelivreeLe: jour(client.pieceDelivreeLe), pieceDelivreeA: client.pieceDelivreeA ?? '',
      permisNumero: client.permisNumero ?? '', permisDelivreLe: jour(client.permisDelivreLe),
      compteATerme: client.compteATerme ?? false,
    }
    : { ...VIDE }));
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const societe = f.type === 'societe';

  async function creer() {
    setErreur(''); setEnvoi(true);
    const corps = {
      ...f,
      email: f.email || undefined,
      dateNaissance: f.dateNaissance || undefined,
      pieceDelivreeLe: f.pieceDelivreeLe || undefined,
      permisDelivreLe: f.permisDelivreLe || undefined,
    };
    try {
      if (modification) {
        const maj = await api.patch<Client>(`/clients/${client!._id}`, corps);
        // Le verdict est recalcule : c'est tout l'interet de la correction.
        const v = await api.post<Verdict>('/clients/verifier', {
          dateNaissance: maj.dateNaissance, permisDelivreLe: maj.permisDelivreLe,
          listeNoire: maj.listeNoire, motifListeNoire: maj.motifListeNoire,
        });
        onCree(maj, v);
      } else {
        const r = await api.post<{ client: Client; verdict: Verdict }>('/clients', corps);
        onCree(r.client, r.verdict);
      }
    } catch (e) {
      setErreur(e instanceof ApiError ? e.erreur : modification ? 'Modification impossible' : 'Création impossible');
    } finally { setEnvoi(false); }
  }

  return (
    <Modale titre={modification ? `Modifier ${client!.nom}` : 'Nouveau client'} onFermer={onFermer}>
      <div style={{ display: 'grid', gap: 12, maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
        <Champ label="Type">
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
            <option value="particulier">Particulier</option><option value="societe">Société</option>
          </select>
        </Champ>

        {societe ? (
          <>
            <Champ label="Raison sociale"><input value={f.raisonSociale} onChange={(e) => setF({ ...f, raisonSociale: e.target.value, nom: e.target.value })} /></Champ>
            <label style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
              <input type="checkbox" checked={f.compteATerme} onChange={(e) => setF({ ...f, compteATerme: e.target.checked })} />
              Compte a terme — paie a 30 jours
            </label>
          </>
        ) : (
          <div className="grille g2">
            <Champ label="Nom"><input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} /></Champ>
            <Champ label="Prénom"><input value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })} /></Champ>
            <Champ label="Date de naissance" aide="Sert au contrôle des 28 ans (article 01).">
              <input type="date" value={f.dateNaissance} onChange={(e) => setF({ ...f, dateNaissance: e.target.value })} />
            </Champ>
            <Champ label="Lieu de naissance"><input value={f.lieuNaissance} onChange={(e) => setF({ ...f, lieuNaissance: e.target.value })} /></Champ>
          </div>
        )}

        <div className="grille g2">
          <Champ label="Téléphone"><input value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} placeholder="0661…" /></Champ>
          <Champ label="E-mail"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Champ>
        </div>
        <Champ label="Adresse"><input value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} /></Champ>

        <div className="grille g2">
          <Champ label="Type de pièce">
            <select value={f.pieceType} onChange={(e) => setF({ ...f, pieceType: e.target.value })}>
              <option value="CIN">Carte d'identité</option><option value="PC">Permis de conduire</option>
              <option value="PSP">Passeport</option><option value="CM">Carte militaire</option>
            </select>
          </Champ>
          <Champ label="N° de la pièce"><input value={f.pieceNumero} onChange={(e) => setF({ ...f, pieceNumero: e.target.value })} /></Champ>
          <Champ label="Delivree le"><input type="date" value={f.pieceDelivreeLe} onChange={(e) => setF({ ...f, pieceDelivreeLe: e.target.value })} /></Champ>
          <Champ label="Delivree a"><input value={f.pieceDelivreeA} onChange={(e) => setF({ ...f, pieceDelivreeA: e.target.value })} /></Champ>
        </div>

        {!societe && (
          <div className="grille g2">
            <Champ label="N° du permis"><input value={f.permisNumero} onChange={(e) => setF({ ...f, permisNumero: e.target.value })} /></Champ>
            <Champ label="Permis délivré le" aide="Sert au contrôle des 5 ans d'ancienneté (article 01).">
              <input type="date" value={f.permisDelivreLe} onChange={(e) => setF({ ...f, permisDelivreLe: e.target.value })} />
            </Champ>
          </div>
        )}
      </div>

      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>Annuler</button>
        <button className="btn btn-p" disabled={envoi || !f.telephone || !f.pieceNumero || !(societe ? f.raisonSociale : f.nom)}
                onClick={() => void creer()}>
          {envoi ? 'Enregistrement…' : modification ? 'Enregistrer les corrections' : 'Créer le client'}
        </button>
      </div>
    </Modale>
  );
}
